import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { SELECTABLE_SELECTOR, selectableUid } from "../utils/selectable";

/** Pointer travel (px) required before a press turns into a marquee drag. */
const DRAG_ACTIVATION_THRESHOLD_PX = 6;

/** Distance from a viewport edge within which the page auto-scrolls. */
const EDGE_SCROLL_ZONE_PX = 64;

/** Maximum auto-scroll speed (px per animation frame) at the very edge. */
const MAX_EDGE_SCROLL_SPEED_PX = 26;

/** Cell rects are cached in document coordinates so they survive scrolling. */
type CachedCell = {
  uid: string;
  left: number;
  top: number;
  right: number;
  bottom: number;
};

type MarqueeOptions = {
  /**
   * Container whose selectable descendants (see {@link SELECTABLE_SELECTOR})
   * participate. Their `getBoundingClientRect`s are snapshotted once when a drag
   * begins, so the `mousemove` / auto-scroll hot-path performs no layout reads.
   */
  containerRef: RefObject<HTMLElement>;
  /** Called (with the full intersecting set) whenever the marquee hit-set changes. */
  onSelect: (uids: Set<string>) => void;
  /** When false, mouse-down is ignored (e.g. there are no files). */
  enabled?: boolean;
};

/**
 * Computes the vertical auto-scroll velocity for a pointer at `clientY`:
 * negative near the top edge, positive near the bottom, zero in between.
 * Speed ramps with how deep the pointer is into the edge zone and is capped.
 */
function edgeScrollVelocity(clientY: number, viewportHeight: number): number {
  const speedFromDepth = (depth: number) => {
    const ratio = Math.min(depth, EDGE_SCROLL_ZONE_PX) / EDGE_SCROLL_ZONE_PX;
    return Math.max(2, Math.round(ratio * MAX_EDGE_SCROLL_SPEED_PX));
  };
  if (clientY < EDGE_SCROLL_ZONE_PX) {
    return -speedFromDepth(EDGE_SCROLL_ZONE_PX - clientY);
  }
  const bottomEdge = viewportHeight - EDGE_SCROLL_ZONE_PX;
  if (clientY > bottomEdge) {
    return speedFromDepth(clientY - bottomEdge);
  }
  return 0;
}

/**
 * Drag-to-select ("marquee" / rubber-band) selection, mirroring desktop file
 * managers: press on empty space in the grid, drag a rectangle, and every cell
 * it intersects becomes the selection. A press on a card or a row belongs to
 * that item and never starts one.
 *
 * When the pointer is dragged into the top/bottom edge zone, the page
 * auto-scrolls in that direction and keeps scrolling — extending the box and
 * growing the selection — for as long as the pointer stays in the zone. The
 * scroll stops the moment the pointer leaves the zone or the drag ends.
 *
 * Performance characteristics:
 *  - The selection rectangle is moved by mutating the box element's style
 *    directly (`boxRef`), so dragging never triggers a React re-render.
 *  - Cell rects are measured once on activation, in document coordinates, so
 *    they stay valid while the page scrolls; the hot-path only does arithmetic.
 *  - `onSelect` fires only when the set of intersecting cells actually changes.
 *
 * Usage: spread `handleMouseDown`/`handleClickCapture` on the container, attach
 * `boxRef` to a `position: fixed` element rendered while `isDragging`.
 */
export function useMarqueeSelection({
  containerRef,
  onSelect,
  enabled = true,
}: MarqueeOptions) {
  const [isDragging, setIsDragging] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // All mutable drag bookkeeping lives in a ref so the handlers stay
  // referentially stable and never schedule a render.
  const drag = useRef({
    // Anchor corner, in document coordinates (scroll-invariant).
    anchorDocX: 0,
    anchorDocY: 0,
    // Latest pointer position, in viewport (client) coordinates.
    lastClientX: 0,
    lastClientY: 0,
    // Client position at mouse-down, for the activation threshold.
    startClientX: 0,
    startClientY: 0,
    active: false,
    cells: [] as CachedCell[],
    lastSignature: "",
  });
  const autoScrollRaf = useRef<number | undefined>(undefined);
  const autoScrollVelocity = useRef(0);
  // Set on `mouseup` after a real drag so the click that immediately follows
  // (on whichever card the pointer is over) can be swallowed.
  const suppressNextClick = useRef(false);

  // Re-projects the document-space box into the fixed (viewport) box element
  // and recomputes the intersecting selection. Safe to call from both the
  // mousemove handler and the auto-scroll frame loop.
  const projectAndSelect = useCallback(() => {
    const d = drag.current;
    if (!d.active) return;

    const { scrollX, scrollY } = window;
    const pointerDocX = d.lastClientX + scrollX;
    const pointerDocY = d.lastClientY + scrollY;

    const minDocX = Math.min(d.anchorDocX, pointerDocX);
    const maxDocX = Math.max(d.anchorDocX, pointerDocX);
    const minDocY = Math.min(d.anchorDocY, pointerDocY);
    const maxDocY = Math.max(d.anchorDocY, pointerDocY);

    const el = boxRef.current;
    if (el) {
      // Fixed element → convert document coords back to viewport coords.
      el.style.transform = `translate(${minDocX - scrollX}px, ${
        minDocY - scrollY
      }px)`;
      el.style.width = `${maxDocX - minDocX}px`;
      el.style.height = `${maxDocY - minDocY}px`;
    }

    const hit: string[] = [];
    for (let i = 0; i < d.cells.length; i++) {
      const c = d.cells[i];
      if (
        c.left <= maxDocX &&
        c.right >= minDocX &&
        c.top <= maxDocY &&
        c.bottom >= minDocY
      ) {
        hit.push(c.uid);
      }
    }

    const signature = hit.join("|");
    if (signature !== d.lastSignature) {
      d.lastSignature = signature;
      onSelect(new Set(hit));
    }
  }, [onSelect]);

  const stopAutoScroll = useCallback(() => {
    autoScrollVelocity.current = 0;
    if (autoScrollRaf.current !== undefined) {
      cancelAnimationFrame(autoScrollRaf.current);
      autoScrollRaf.current = undefined;
    }
  }, []);

  const runAutoScroll = useCallback(() => {
    const tick = () => {
      const d = drag.current;
      const velocity = autoScrollVelocity.current;
      if (!d.active || velocity === 0) {
        autoScrollRaf.current = undefined;
        return;
      }
      window.scrollBy(0, velocity);
      // Pointer is stationary while auto-scrolling, but the growing scroll
      // offset extends the box and pulls in more cells.
      projectAndSelect();
      autoScrollRaf.current = requestAnimationFrame(tick);
    };
    if (autoScrollRaf.current === undefined) {
      autoScrollRaf.current = requestAnimationFrame(tick);
    }
  }, [projectAndSelect]);

  // Updates the auto-scroll velocity from the current pointer position and
  // starts/stops the frame loop accordingly.
  const updateAutoScroll = useCallback(() => {
    const velocity = edgeScrollVelocity(
      drag.current.lastClientY,
      window.innerHeight
    );
    autoScrollVelocity.current = velocity;
    if (velocity !== 0) runAutoScroll();
    else stopAutoScroll();
  }, [runAutoScroll, stopAutoScroll]);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const d = drag.current;
      d.lastClientX = e.clientX;
      d.lastClientY = e.clientY;

      if (!d.active) {
        if (
          Math.abs(e.clientX - d.startClientX) < DRAG_ACTIVATION_THRESHOLD_PX &&
          Math.abs(e.clientY - d.startClientY) < DRAG_ACTIVATION_THRESHOLD_PX
        ) {
          return;
        }
        // Activate: snapshot every cell's rect once, in document coordinates.
        const { scrollX, scrollY } = window;
        const nodes =
          containerRef.current?.querySelectorAll<HTMLElement>(
            SELECTABLE_SELECTOR
          );
        const cells: CachedCell[] = [];
        nodes?.forEach((node) => {
          const r = node.getBoundingClientRect();
          cells.push({
            uid: selectableUid(node) as string,
            left: r.left + scrollX,
            top: r.top + scrollY,
            right: r.right + scrollX,
            bottom: r.bottom + scrollY,
          });
        });
        d.cells = cells;
        d.active = true;
        d.lastSignature = " "; // guarantees the first onSelect fires
        setIsDragging(true);
      }

      projectAndSelect();
      updateAutoScroll();
    },
    [containerRef, projectAndSelect, updateAutoScroll]
  );

  const endDrag = useCallback(() => {
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", endDrag);
    stopAutoScroll();
    const d = drag.current;
    if (d.active) {
      suppressNextClick.current = true;
      d.active = false;
      setIsDragging(false);
    }
  }, [handleMouseMove, stopAutoScroll]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!enabled || e.button !== 0) return;
      // Only empty space starts a marquee. A press that lands on a card or a row
      // is that item's own click gesture (a plain click selects it), so the few
      // px of jitter while clicking must not turn into a box that replaces the
      // selection with whatever it happened to cross. Dragging a file is not
      // something we support, and it is learned behaviour people avoid on top of
      // a tile anyway, so the only marquee this gives up is the accidental one.
      if (
        e.target instanceof Element &&
        e.target.closest(SELECTABLE_SELECTOR)
      ) {
        return;
      }
      // Clear any stale flag from a drag that ended off-grid (no click followed).
      suppressNextClick.current = false;
      const d = drag.current;
      d.startClientX = e.clientX;
      d.startClientY = e.clientY;
      d.lastClientX = e.clientX;
      d.lastClientY = e.clientY;
      d.anchorDocX = e.clientX + window.scrollX;
      d.anchorDocY = e.clientY + window.scrollY;
      d.active = false;
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", endDrag);
    },
    [enabled, handleMouseMove, endDrag]
  );

  const handleClickCapture = useCallback((e: React.MouseEvent) => {
    if (suppressNextClick.current) {
      suppressNextClick.current = false;
      e.stopPropagation();
      e.preventDefault();
    }
  }, []);

  // Detach listeners / cancel the scroll loop if the component unmounts mid-drag.
  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", endDrag);
      stopAutoScroll();
    };
  }, [handleMouseMove, endDrag, stopAutoScroll]);

  return { isDragging, boxRef, handleMouseDown, handleClickCapture };
}
